import utils from '@eitje/utils'
import _ from 'lodash'
import {useRef} from 'react'
import {Tooltip} from '../tooltip'
import {
	getAbsolute,
	getBorder,
	getBorderRadius,
	getDataAttributes,
	getDistribution,
	getFixed,
	getHover,
	getMargin,
	getPadding,
	getSticky,
	useMergedRefs,
} from './helpers'
import './styles/index.less'
import {trimNamedStyles, trimPixelStyles} from './trim'

const getTooltipTranslation = ({disabled, tooltip}) => {
	if (_.isString(disabled)) return disabled
	if (_.isString(tooltip)) return tooltip
	if (tooltip?.key) return tooltip
	if (disabled?.key) return disabled
}

export const BaseLayout = ({ref, ...props}) => {
	let {
		children,
		id,
		className,
		onClick = _.noop,

		// Size
		height,
		minHeight,
		maxHeight,
		width,
		minWidth,
		maxWidth,
		grow,

		// Distribute content
		direction = 'horizontal',
		horizontal,
		vertical,
		wrap,

		// Position self
		absolute,
		fixed,
		sticky,

		// Block level
		relative,
		block,

		// Color
		baseColor = 'blue',
		background,
		colorSet = !!background,
		active,

		// Borders
		border,
		borderLeft,
		borderRight,
		borderTop,
		borderBottom,

		// Style
		style = {},
		margin,
		padding,
		borderRadius,
		gap = 4,
		type,

		// Interact
		disabled,
		tooltip,
		debug,
		hasHover,

		// floating-ui internals
		onClickCapture,
		onFocus,
		onMouseEnter,
		onMouseLeave,
		onMouseDown,
		onMouseUp,
		onMouseMove,
		onPointerEnter,
		onPointerDown,
		onPointerMove,
		onPointerLeave,
		onPointerDownCapture,
		onKeyDown,
		onKeyUp,
		tabIndex,

		...rest
	} = props

	const localRef = useRef()
	const mergedRef = useMergedRefs(localRef, ref)
	const dataAttributes = getDataAttributes(rest)
	const position = getFixed(fixed) || getAbsolute(absolute) || getSticky(sticky)
	margin = getMargin(margin)
	padding = getPadding(padding)
	border = getBorder({border, borderLeft, borderRight, borderTop, borderBottom})
	borderRadius = getBorderRadius(borderRadius)

	if (disabled) {
		colorSet = 'disabled'
		onClick = undefined
		baseColor = 'grey'
	}

	let {styles, classNames} = trimNamedStyles({
		minHeight,
		maxHeight,
		minWidth,
		maxWidth,
		...getDistribution({...props, direction}),
		overflowY: maxHeight && 'auto',
		overflowX: maxHeight && 'hidden',
		position: relative && 'relative',
		display: block && 'block',
		...margin,
		...padding,
		...position,
		...style,
	})

	classNames = [
		className,
		'layout',
		baseColor && `bc-${baseColor}`,
		colorSet !== true && colorSet,
		colorSet && 'cs',
		active && 'active',
		debug && 'debug',
		background && `bg-${background}`,
		background && 'bg',
		grow && 'grow',
		horizontal === 'last-item-end' && 'last-item-end',
		type === 'card' && 'card',
		getHover({localRef, ...props}),
		...border,
		...classNames,
	]

	const trimmed = trimPixelStyles({
		styles,
		classNames,
		width,
		minWidth,
		maxWidth,
		height,
		minHeight,
		maxHeight,
		gap,
		...margin,
		...padding,
		...position,
		borderRadius,
	})
	styles = trimmed.styles
	classNames = utils.makeCns(trimmed.classNames)

	// These props are generated by floating-ui and required to make all the dropdowns, selects, popouts and tooltips work.
	const floatingUiProps = {
		onClickCapture,
		onFocus,
		onMouseEnter,
		onMouseLeave,
		onMouseDown,
		onMouseUp,
		onMouseMove,
		onPointerEnter,
		onPointerDown,
		onPointerMove,
		onPointerLeave,
		onPointerDownCapture,
		onKeyDown,
		onKeyUp,
		tabIndex: disabled ? -1 : tabIndex,
	}

	const content = (
		<div id={id} className={classNames} ref={mergedRef} {...dataAttributes} onClick={onClick} style={styles} {...floatingUiProps}>
			{children}
		</div>
	)

	const tooltipTranslation = getTooltipTranslation({disabled, tooltip})
	if (tooltipTranslation) return <Tooltip trigger={content} t={tooltipTranslation} />

	return content
}
